import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  SesionCajaResponse,
  OrdenPosResponse,
  AbrirCajaRequest,
  CerrarCajaRequest,
  CrearOrdenRequest,
  AgregarItemRequest,
  EmitirOrdenRequest,
} from '../types/pos.types';
import { PosService } from '../services/posService';
import { getErrorMessage } from '../../../services/errorService';

interface PosState {
  // Session
  sesionActiva: SesionCajaResponse | null;
  sesionLoading: boolean;

  // Current Order
  ordenActual: OrdenPosResponse | null;
  ordenes: OrdenPosResponse[];
  ordenLoading: boolean;
  ordenSaving: boolean;

  // UI state
  paymentDialogOpen: boolean;
  cajaDialogOpen: boolean;
  cashMovementDialogOpen: boolean;

  // Error
  error: string | null;

  // Actions - Session
  fetchSesionActiva: () => Promise<void>;
  abrirSesion: (data: AbrirCajaRequest) => Promise<SesionCajaResponse>;
  cerrarSesion: (data: CerrarCajaRequest) => Promise<SesionCajaResponse>;

  // Actions - Order
  crearOrden: (data: CrearOrdenRequest) => Promise<void>;
  agregarItem: (data: AgregarItemRequest) => Promise<void>;
  actualizarItem: (idDetalle: number, data: AgregarItemRequest) => Promise<void>;
  eliminarItem: (idDetalle: number) => Promise<void>;
  emitirOrden: (data: EmitirOrdenRequest) => Promise<any>;
  cancelarOrden: () => Promise<void>;
  fetchOrdenes: () => Promise<void>;
  seleccionarOrden: (id: number) => Promise<void>;

  // Actions - UI
  setPaymentDialogOpen: (open: boolean) => void;
  setCajaDialogOpen: (open: boolean) => void;
  setCashMovementDialogOpen: (open: boolean) => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  sesionActiva: null,
  sesionLoading: false,
  ordenActual: null,
  ordenes: [],
  ordenLoading: false,
  ordenSaving: false,
  paymentDialogOpen: false,
  cajaDialogOpen: false,
  cashMovementDialogOpen: false,
  error: null,
};

export const usePosStore = create<PosState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // === Session Actions ===
      fetchSesionActiva: async () => {
        set({ sesionLoading: true, error: null }, false, 'pos/fetchSesion/start');
        try {
          const sesion = await PosService.getSesionActiva();
          set({ sesionActiva: sesion, sesionLoading: false }, false, 'pos/fetchSesion/success');
        } catch (error) {
          set({
            sesionLoading: false,
            error: getErrorMessage(error, 'Error al obtener sesion'),
          }, false, 'pos/fetchSesion/error');
        }
      },

      abrirSesion: async (data: AbrirCajaRequest) => {
        set({ sesionLoading: true, error: null }, false, 'pos/abrirSesion/start');
        try {
          const sesion = await PosService.abrirSesion(data);
          set({ sesionActiva: sesion, sesionLoading: false, cajaDialogOpen: false }, false, 'pos/abrirSesion/success');
          return sesion;
        } catch (error) {
          set({
            sesionLoading: false,
            error: getErrorMessage(error, 'Error al abrir sesion'),
          }, false, 'pos/abrirSesion/error');
          throw error;
        }
      },

      cerrarSesion: async (data: CerrarCajaRequest) => {
        set({ sesionLoading: true, error: null }, false, 'pos/cerrarSesion/start');
        try {
          const sesion = await PosService.cerrarSesion(data);
          set({ sesionActiva: null, sesionLoading: false, ordenActual: null, ordenes: [] }, false, 'pos/cerrarSesion/success');
          return sesion;
        } catch (error) {
          set({
            sesionLoading: false,
            error: getErrorMessage(error, 'Error al cerrar sesion'),
          }, false, 'pos/cerrarSesion/error');
          throw error;
        }
      },

      // === Order Actions ===
      crearOrden: async (data: CrearOrdenRequest) => {
        set({ ordenSaving: true, error: null }, false, 'pos/crearOrden/start');
        try {
          const orden = await PosService.crearOrden(data);
          set((state) => ({
            ordenActual: orden,
            ordenes: [...state.ordenes, orden],
            ordenSaving: false,
          }), false, 'pos/crearOrden/success');
        } catch (error) {
          set({
            ordenSaving: false,
            error: getErrorMessage(error, 'Error al crear orden'),
          }, false, 'pos/crearOrden/error');
          throw error;
        }
      },

      agregarItem: async (data: AgregarItemRequest) => {
        const { ordenActual } = get();
        if (!ordenActual) throw new Error('No hay orden activa');

        set({ ordenSaving: true, error: null }, false, 'pos/agregarItem/start');
        try {
          const orden = await PosService.agregarItem(ordenActual.idOrdenPos, data);
          set((state) => ({
            ordenActual: orden,
            ordenes: state.ordenes.map((o) =>
              o.idOrdenPos === orden.idOrdenPos ? orden : o
            ),
            ordenSaving: false,
          }), false, 'pos/agregarItem/success');
        } catch (error) {
          set({
            ordenSaving: false,
            error: getErrorMessage(error, 'Error al agregar item'),
          }, false, 'pos/agregarItem/error');
          throw error;
        }
      },

      actualizarItem: async (idDetalle: number, data: AgregarItemRequest) => {
        const { ordenActual } = get();
        if (!ordenActual) throw new Error('No hay orden activa');

        set({ ordenSaving: true, error: null }, false, 'pos/actualizarItem/start');
        try {
          const orden = await PosService.actualizarItem(ordenActual.idOrdenPos, idDetalle, data);
          set((state) => ({
            ordenActual: orden,
            ordenes: state.ordenes.map((o) =>
              o.idOrdenPos === orden.idOrdenPos ? orden : o
            ),
            ordenSaving: false,
          }), false, 'pos/actualizarItem/success');
        } catch (error) {
          set({
            ordenSaving: false,
            error: getErrorMessage(error, 'Error al actualizar item'),
          }, false, 'pos/actualizarItem/error');
          throw error;
        }
      },

      eliminarItem: async (idDetalle: number) => {
        const { ordenActual } = get();
        if (!ordenActual) throw new Error('No hay orden activa');

        set({ ordenSaving: true, error: null }, false, 'pos/eliminarItem/start');
        try {
          const orden = await PosService.eliminarItem(ordenActual.idOrdenPos, idDetalle);
          set((state) => ({
            ordenActual: orden,
            ordenes: state.ordenes.map((o) =>
              o.idOrdenPos === orden.idOrdenPos ? orden : o
            ),
            ordenSaving: false,
          }), false, 'pos/eliminarItem/success');
        } catch (error) {
          set({
            ordenSaving: false,
            error: getErrorMessage(error, 'Error al eliminar item'),
          }, false, 'pos/eliminarItem/error');
          throw error;
        }
      },

      emitirOrden: async (data: EmitirOrdenRequest) => {
        const { ordenActual } = get();
        if (!ordenActual) throw new Error('No hay orden activa');

        set({ ordenSaving: true, error: null }, false, 'pos/emitirOrden/start');
        try {
          const result = await PosService.emitirOrden(ordenActual.idOrdenPos, data);
          set((state) => ({
            ordenActual: null,
            ordenes: state.ordenes.filter((o) => o.idOrdenPos !== ordenActual.idOrdenPos),
            ordenSaving: false,
            paymentDialogOpen: false,
          }), false, 'pos/emitirOrden/success');
          return result;
        } catch (error) {
          set({
            ordenSaving: false,
            error: getErrorMessage(error, 'Error al emitir orden'),
          }, false, 'pos/emitirOrden/error');
          throw error;
        }
      },

      cancelarOrden: async () => {
        const { ordenActual } = get();
        if (!ordenActual) return;

        set({ ordenSaving: true, error: null }, false, 'pos/cancelarOrden/start');
        try {
          await PosService.cancelarOrden(ordenActual.idOrdenPos);
          set((state) => ({
            ordenActual: null,
            ordenes: state.ordenes.filter((o) => o.idOrdenPos !== ordenActual.idOrdenPos),
            ordenSaving: false,
          }), false, 'pos/cancelarOrden/success');
        } catch (error) {
          set({
            ordenSaving: false,
            error: getErrorMessage(error, 'Error al cancelar orden'),
          }, false, 'pos/cancelarOrden/error');
          throw error;
        }
      },

      fetchOrdenes: async () => {
        set({ ordenLoading: true, error: null }, false, 'pos/fetchOrdenes/start');
        try {
          const ordenes = await PosService.getOrdenes();
          set({ ordenes, ordenLoading: false }, false, 'pos/fetchOrdenes/success');
        } catch (error) {
          set({
            ordenLoading: false,
            error: getErrorMessage(error, 'Error al obtener ordenes'),
          }, false, 'pos/fetchOrdenes/error');
        }
      },

      seleccionarOrden: async (id: number) => {
        set({ ordenLoading: true, error: null }, false, 'pos/seleccionarOrden/start');
        try {
          const orden = await PosService.getOrden(id);
          set({ ordenActual: orden, ordenLoading: false }, false, 'pos/seleccionarOrden/success');
        } catch (error) {
          set({
            ordenLoading: false,
            error: getErrorMessage(error, 'Error al seleccionar orden'),
          }, false, 'pos/seleccionarOrden/error');
        }
      },

      // === UI Actions ===
      setPaymentDialogOpen: (open) => set({ paymentDialogOpen: open }, false, 'pos/ui/paymentDialog'),
      setCajaDialogOpen: (open) => set({ cajaDialogOpen: open }, false, 'pos/ui/cajaDialog'),
      setCashMovementDialogOpen: (open) => set({ cashMovementDialogOpen: open }, false, 'pos/ui/cashMovementDialog'),
      clearError: () => set({ error: null }, false, 'pos/clearError'),
      reset: () => set(initialState, false, 'pos/reset'),
    }),
    { name: 'pos-store' }
  )
);
